import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { PlusCircle, Trash2, Pencil, X, Layers, Wrench, Search, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { mcpResourcesService, type MCPResourceRow, type MCPDataModelOption } from "./mcpResourcesService";
import { featureHelperService, type KnowledgeBaseListItem } from "./featureHelperService";
import { mcpToolRegistrationsService, type MCPToolRegistrationRow } from "./mcpToolRegistrationsService";
import { useTheme } from "../../../../components/common/Theme/ThemeContext";
import "./MCPResourcesV2/mcpResourcesV2.css";

// ── data ───────────────────────────────────────────────────────────
/** MCP resource register / edit: only these two categories in the UI. */
const RESOURCE_CATEGORIES = ["DataModel", "KnowledgeBase"] as const;

const KNOWLEDGE_BASE_CATEGORY = "KnowledgeBase";

function normalizeResourceCategory(cat: string | undefined): string {
  const c = cat?.trim();
  if (c === KNOWLEDGE_BASE_CATEGORY) return KNOWLEDGE_BASE_CATEGORY;
  if (c === "DataModel") return "DataModel";
  return RESOURCE_CATEGORIES[0];
}

/** MCP tool register / edit: only these two categories in the UI. */
const TOOL_CATEGORIES = ["API", "MCP Tools Action"] as const;

function normalizeToolCategory(cat: string | undefined): string {
  const c = cat?.trim();
  if (c === "MCP Tools Action") return "MCP Tools Action";
  if (c === "API") return "API";
  return TOOL_CATEGORIES[0];
}

type ListSection = "resources" | "tools";

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const DEFAULT_PAGE_SIZE = 10;

function formatSchemaJson(schema: unknown): string {
  if (schema == null) return "";
  try {
    return JSON.stringify(schema, null, 2);
  } catch {
    return String(schema);
  }
}

// ── MCPResources ────────────────────────────────────────────────────
export default function MCPResources() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const [listSection, setListSection] = useState<ListSection>("resources");
  const [showForm, setShowForm] = useState(false);
  const [editingResourceId, setEditingResourceId] = useState<string | null>(null);
  const [showToolForm, setShowToolForm] = useState(false);
  const [editingToolId, setEditingToolId] = useState<string | null>(null);
  const [resourceRows, setResourceRows] = useState<MCPResourceRow[]>([]);
  const [toolRows, setToolRows] = useState<MCPToolRegistrationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittingTool, setSubmittingTool] = useState(false);

  // register form state
  const [dataModelId, setDataModelId] = useState("");
  const [resCat, setResCat] = useState<string>(RESOURCE_CATEGORIES[0]);
  const [resName, setResName] = useState("");
  const [resDesc, setResDesc] = useState("");
  const [resourceSchemaText, setResourceSchemaText] = useState("");
  const [resIsActive, setResIsActive] = useState(true);
  const [flash, setFlash] = useState(false);
  const [dataModels, setDataModels] = useState<MCPDataModelOption[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseListItem[]>([]);
  const [resourceOptionsLoading, setResourceOptionsLoading] = useState(false);
  const [resourceOptionsError, setResourceOptionsError] = useState<string | null>(null);
  const [toolFlash, setToolFlash] = useState(false);
  const [toolDisplayName, setToolDisplayName] = useState("");
  const [toolCategory, setToolCategory] = useState<string>(TOOL_CATEGORIES[0]);
  const [toolEndpoint, setToolEndpoint] = useState("");
  const [toolDescription, setToolDescription] = useState("");
  const [toolIsActive, setToolIsActive] = useState(true);

  // confirm toggle (Is Active) before applying
  const [confirmToggle, setConfirmToggle] = useState<{ id: string; newValue: boolean } | null>(null);
  // confirm delete (assignedTokenCount set when opening to show warning)
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string; assignedTokenCount: number } | null>(null);
  const [deleteToolConfirm, setDeleteToolConfirm] = useState<{ id: string; name: string } | null>(null);
  const [resourceSearch, setResourceSearch] = useState("");
  const [toolSearch, setToolSearch] = useState("");
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [resourcePage, setResourcePage] = useState(1);
  const [toolPage, setToolPage] = useState(1);

  const fetchCatalog = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    const errs: string[] = [];
    try {
      const [resSettled, toolsSettled] = await Promise.allSettled([
        mcpResourcesService.getAll(),
        mcpToolRegistrationsService.list(),
      ]);
      if (resSettled.status === "fulfilled") setResourceRows(resSettled.value);
      else {
        setResourceRows([]);
        errs.push(
          resSettled.reason instanceof Error ? resSettled.reason.message : "Failed to load MCP resources"
        );
      }
      if (toolsSettled.status === "fulfilled") setToolRows(toolsSettled.value);
      else {
        setToolRows([]);
        errs.push(
          toolsSettled.reason instanceof Error ? toolsSettled.reason.message : "Failed to load tool registrations"
        );
      }
      if (errs.length) setError(errs.join(" · "));
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  useEffect(() => {
    if (!showForm) return;
    let cancelled = false;
    (async () => {
      setResourceOptionsLoading(true);
      setResourceOptionsError(null);
      try {
        if (resCat === KNOWLEDGE_BASE_CATEGORY) {
          const list = await featureHelperService.listKnowledgeBases();
          if (!cancelled) {
            setKnowledgeBases(list);
            setDataModels([]);
          }
        } else {
          const list = await mcpResourcesService.getDataModels();
          if (!cancelled) {
            setDataModels(list);
            setKnowledgeBases([]);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setDataModels([]);
          setKnowledgeBases([]);
          setResourceOptionsError(
            e instanceof Error
              ? e.message
              : resCat === KNOWLEDGE_BASE_CATEGORY
                ? "Failed to load knowledge bases"
                : "Failed to load data models"
          );
        }
      } finally {
        if (!cancelled) setResourceOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showForm, resCat]);

  useEffect(() => {
    if (!showForm || !editingResourceId) return;
    let cancelled = false;
    (async () => {
      const detail = await mcpResourcesService.getById(editingResourceId);
      if (cancelled || !detail) return;
      setResName(detail.name);
      setResDesc(detail.description ?? "");
      setResIsActive(detail.isActive);
      setResCat(normalizeResourceCategory(detail.cat !== "—" ? detail.cat : undefined));
      if (detail.resourceSchema != null) setResourceSchemaText(detail.resourceSchema);
      setDataModelId(detail.resourceId || detail.dataModelId || "");
    })();
    return () => {
      cancelled = true;
    };
  }, [showForm, editingResourceId]);

  const openCreateResource = () => {
    setEditingResourceId(null);
    setDataModelId("");
    setResourceSchemaText("");
    setResName("");
    setResDesc("");
    setResCat(RESOURCE_CATEGORIES[0]);
    setResIsActive(true);
    setShowForm(true);
  };

  const openEditResource = (r: MCPResourceRow) => {
    setEditingResourceId(r.id);
    setResName(r.name);
    setResDesc(r.description ?? "");
    setResIsActive(r.isActive);
    setResCat(normalizeResourceCategory(r.cat !== "—" ? r.cat : undefined));
    setDataModelId(r.resourceId || r.dataModelId || "");
    setResourceSchemaText(r.resourceSchema ?? "");
    setShowForm(true);
  };

  const resetRegisterForm = () => {
    setEditingResourceId(null);
    setDataModelId("");
    setResourceSchemaText("");
    setResName("");
    setResDesc("");
    setResCat(RESOURCE_CATEGORIES[0]);
    setResIsActive(true);
  };

  const closeResourceForm = () => {
    if (flash) return;
    setShowForm(false);
    setEditingResourceId(null);
  };

  const onResourcePick = (id: string) => {
    setDataModelId(id);
    if (resCat === KNOWLEDGE_BASE_CATEGORY) {
      const trimmed = id.trim();
      setResourceSchemaText(trimmed ? formatSchemaJson({ kbid: trimmed }) : "");
    } else {
      const m = dataModels.find((d) => d.id === id);
      setResourceSchemaText(m ? formatSchemaJson(m.schema) : "");
    }
  };

  const handleSubmit = async () => {
    if (!resName?.trim() || submitting) return;
    if (!editingResourceId && !dataModelId.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const existingRow = editingResourceId
        ? resourceRows.find((r) => r.id === editingResourceId)
        : undefined;
      const selectedResourceId = dataModelId.trim();
      const resolvedResourceId = selectedResourceId || existingRow?.resourceId?.trim() || "";
      if (!resolvedResourceId) return;
      const resolvedDataModelId =
        resCat === KNOWLEDGE_BASE_CATEGORY
          ? undefined
          : selectedResourceId || existingRow?.dataModelId?.trim() || undefined;
      const base = {
        name: resName.trim(),
        category: resCat,
        description: resDesc.trim(),
        resourceSchema: resourceSchemaText.trim(),
        isActive: resIsActive,
        resourceId: resolvedResourceId,
      };
      if (editingResourceId) {
        await mcpResourcesService.updateResource(editingResourceId, {
          ...base,
          dataModelId: resolvedDataModelId,
        });
      } else {
        await mcpResourcesService.register({
          ...base,
          dataModelId: resolvedDataModelId ?? "",
        });
      }
      await fetchCatalog({ silent: true });
      setFlash(true);
      setTimeout(() => {
        setFlash(false);
        setShowForm(false);
        resetRegisterForm();
      }, 1400);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save resource");
    } finally {
      setSubmitting(false);
    }
  };

  const resetToolForm = () => {
    setEditingToolId(null);
    setToolDisplayName("");
    setToolCategory(TOOL_CATEGORIES[0]);
    setToolEndpoint("");
    setToolDescription("");
    setToolIsActive(true);
  };

  const openCreateTool = () => {
    setEditingToolId(null);
    setToolDisplayName("");
    setToolCategory(TOOL_CATEGORIES[0]);
    setToolEndpoint("");
    setToolDescription("");
    setToolIsActive(true);
    setShowToolForm(true);
  };

  const openEditTool = (t: MCPToolRegistrationRow) => {
    setEditingToolId(t.id);
    setToolDisplayName(t.displayName);
    setToolCategory(normalizeToolCategory(t.category));
    setToolEndpoint(t.endpoint);
    setToolDescription(t.description ?? "");
    setToolIsActive(t.isActive);
    setShowToolForm(true);
  };

  const closeToolForm = () => {
    if (toolFlash) return;
    setShowToolForm(false);
    setEditingToolId(null);
  };

  const handleToolSubmit = async () => {
    if (!toolDisplayName.trim() || !toolEndpoint.trim() || submittingTool) return;
    setSubmittingTool(true);
    setError(null);
    try {
      const payload = {
        displayName: toolDisplayName.trim(),
        category: toolCategory,
        endpoint: toolEndpoint.trim(),
        description: toolDescription.trim(),
        isActive: toolIsActive,
      };
      if (editingToolId) {
        await mcpToolRegistrationsService.update(editingToolId, payload);
      } else {
        await mcpToolRegistrationsService.register(payload);
      }
      const tools = await mcpToolRegistrationsService.list();
      setToolRows(tools);
      setToolFlash(true);
      setTimeout(() => {
        setToolFlash(false);
        setShowToolForm(false);
        resetToolForm();
      }, 1400);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save tool");
    } finally {
      setSubmittingTool(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await mcpResourcesService.delete(id);
      setResourceRows((p) => p.filter((r) => r.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete resource");
    }
  };

  const handleDeleteTool = async (id: string) => {
    try {
      await mcpToolRegistrationsService.delete(id);
      setToolRows((p) => p.filter((t) => t.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete tool");
    }
  };

  const applyToggleResourceActive = async () => {
    if (!confirmToggle) return;
    try {
      const updated = await mcpResourcesService.update(confirmToggle.id, { isActive: confirmToggle.newValue });
      if (updated) {
        setResourceRows((p) =>
          p.map((r) => (r.id === confirmToggle.id ? { ...r, isActive: updated.isActive } : r))
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update resource");
    }
    setConfirmToggle(null);
  };

  const filteredResourceRows = useMemo(() => {
    const q = resourceSearch.trim().toLowerCase();
    if (!q) return resourceRows;
    return resourceRows.filter((r) =>
      [r.resourceId, r.name, r.cat, r.type, r.description ?? ""].join(" ").toLowerCase().includes(q)
    );
  }, [resourceRows, resourceSearch]);

  const filteredToolRows = useMemo(() => {
    const q = toolSearch.trim().toLowerCase();
    if (!q) return toolRows;
    return toolRows.filter((t) =>
      [t.toolId, t.displayName, t.category, t.endpoint, t.description ?? ""].join(" ").toLowerCase().includes(q)
    );
  }, [toolRows, toolSearch]);

  const resourceTotalPages = Math.max(1, Math.ceil(filteredResourceRows.length / pageSize));
  const toolTotalPages = Math.max(1, Math.ceil(filteredToolRows.length / pageSize));

  const paginatedResourceRows = useMemo(() => {
    const start = (resourcePage - 1) * pageSize;
    return filteredResourceRows.slice(start, start + pageSize);
  }, [filteredResourceRows, resourcePage, pageSize]);

  const paginatedToolRows = useMemo(() => {
    const start = (toolPage - 1) * pageSize;
    return filteredToolRows.slice(start, start + pageSize);
  }, [filteredToolRows, toolPage, pageSize]);

  useEffect(() => {
    setResourcePage(1);
  }, [resourceSearch, pageSize]);

  useEffect(() => {
    setToolPage(1);
  }, [toolSearch, pageSize]);

  useEffect(() => {
    if (resourcePage > resourceTotalPages) setResourcePage(resourceTotalPages);
  }, [resourcePage, resourceTotalPages]);

  useEffect(() => {
    if (toolPage > toolTotalPages) setToolPage(toolTotalPages);
  }, [toolPage, toolTotalPages]);

  const activeSearch = listSection === "resources" ? resourceSearch : toolSearch;
  const setActiveSearch = listSection === "resources" ? setResourceSearch : setToolSearch;
  const activeFilteredCount = listSection === "resources" ? filteredResourceRows.length : filteredToolRows.length;
  const activeTotalCount = listSection === "resources" ? resourceRows.length : toolRows.length;
  const activePage = listSection === "resources" ? resourcePage : toolPage;
  const setActivePage = listSection === "resources" ? setResourcePage : setToolPage;
  const activeTotalPages = listSection === "resources" ? resourceTotalPages : toolTotalPages;
  const activeRangeStart = activeFilteredCount === 0 ? 0 : (activePage - 1) * pageSize + 1;
  const activeRangeEnd = Math.min(activePage * pageSize, activeFilteredCount);

  const searchInputClass = `w-full rounded-lg border pl-9 pr-3 py-2 text-xs outline-none transition-all focus:ring-1 ${isLight
    ? "border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400 focus:ring-indigo-500 shadow-sm"
    : "border-neutral-700 bg-neutral-800 text-gray-200 placeholder:text-gray-500 focus:ring-indigo-500"
    }`;

  const pageSizeSelectClass = `rounded-lg border px-2 py-1.5 text-xs outline-none transition-all focus:ring-1 ${isLight
    ? "border-neutral-300 bg-white text-neutral-700 focus:ring-indigo-500"
    : "border-neutral-700 bg-neutral-800 text-gray-300 focus:ring-indigo-500"
    }`;

  const paginationBtnClass = `inline-flex items-center justify-center rounded-md border p-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isLight
    ? "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50"
    : "border-neutral-700 bg-neutral-800 text-gray-300 hover:bg-neutral-700"
    }`;

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col p-6 pb-8 text-sm text-gray-300">

      <button
        type="button"
        className="mcp-v2-beta-bar"
        onClick={() => navigate("/portal/mcpresources-v2")}
      >
        <span className="mcp-v2-beta-bar__icon" aria-hidden>
          <Sparkles />
        </span>
        <span className="mcp-v2-beta-bar__content">
          <span className="mcp-v2-beta-bar__title">
            MCP Resources v2 is now available
            <span className="mcp-v2-beta-bar__badge">Beta</span>
          </span>
          <span className="mcp-v2-beta-bar__description">
            Connect MCP servers, test them, and save discovered tools and resources.
          </span>
        </span>
        <span className="mcp-v2-beta-bar__action">
          Open v2
          <ChevronRight className="w-4 h-4" aria-hidden />
        </span>
      </button>

      {/* ── header ──────────────────────────────────────────────── */}
      <div className="mb-6 flex shrink-0 flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white">MCP Resources</h2>
          
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={openCreateResource}
            className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm shadow transition-colors"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Register New Resource
          </button>
          <button
            type="button"
            onClick={openCreateTool}
            className={`flex items-center px-4 py-2 rounded text-sm shadow transition-colors border ${isLight
              ? "border-violet-300 bg-violet-600 text-white hover:bg-violet-700"
              : "border-violet-600 bg-violet-700 text-white hover:bg-violet-600"
              }`}
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Register New Tool
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 shrink-0 rounded border border-red-800 bg-red-900/30 px-4 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      {/* ── registration form popup ───────────────────────────────── */}
      {showForm && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
          onClick={closeResourceForm}
        >
          <div
            className={`${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'} border rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col`}
            onClick={e => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-4 py-3 ${isLight ? 'bg-neutral-50 border-neutral-200' : 'bg-neutral-900 border-neutral-800'} border-b flex-shrink-0`}>
              <div>
                <h3 className={`font-semibold ${isLight ? 'text-neutral-900' : 'text-white'} text-sm`}>
                  {editingResourceId ? "Edit MCP Resource" : "Register MCP Resource"}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {editingResourceId
                    ? "Change fields below and save your updates."
                    : "Set category, name, and resource (data model), then review schema and details."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeResourceForm}
                className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1">
              <div className="space-y-5">

                <div>
                  <label className={`block text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'} font-medium mb-2`}>Category</label>
                  <select
                    value={resCat}
                    onChange={(e) => {
                      const next = e.target.value;
                      const wasKb = resCat === KNOWLEDGE_BASE_CATEGORY;
                      const willKb = next === KNOWLEDGE_BASE_CATEGORY;
                      if (wasKb !== willKb) {
                        setDataModelId("");
                        setResourceSchemaText("");
                      }
                      setResCat(next);
                    }}
                    className={`w-full ${isLight ? 'bg-white border-neutral-300 text-neutral-900 shadow-sm' : 'bg-neutral-800 border-neutral-700 text-gray-200'} rounded px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-indigo-500 transition-all`}
                  >
                    {resCat && !(RESOURCE_CATEGORIES as readonly string[]).includes(resCat) ? (
                      <option value={resCat}>{resCat}</option>
                    ) : null}
                    {RESOURCE_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'} font-medium mb-2`}>Name *</label>
                  <input
                    className={`w-full ${isLight ? 'bg-white border-neutral-300 text-neutral-900' : 'bg-neutral-800 border-neutral-700 text-gray-200'} rounded px-3 py-2 text-xs placeholder:text-gray-400 outline-none focus:ring-1 focus:ring-indigo-500 transition-all shadow-sm`}
                    placeholder="Display name for this MCP resource"
                    value={resName}
                    onChange={(e) => setResName(e.target.value)}
                  />
                </div>

                <div>
                  <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'} font-medium mb-2`}>
                    Resource{editingResourceId ? "" : " *"}
                  </p>
                  <p className={`text-[11px] ${isLight ? 'text-neutral-500' : 'text-gray-500'} mb-2`}>
                    {resCat === KNOWLEDGE_BASE_CATEGORY ? (
                      <>
                        Choose one of your knowledge bases. The schema is prefilled with a reference to the selected base (you can edit it).
                        {editingResourceId ? " Optional when updating if the link is unchanged." : ""}
                      </>
                    ) : (
                      <>
                        Choose a data model from the catalog; its definition is copied into the schema field for you to review or adjust.
                        {editingResourceId ? " Optional when updating if the link is unchanged." : ""}
                      </>
                    )}
                  </p>
                  {resourceOptionsError && (
                    <div className={`mb-2 rounded border px-3 py-2 text-xs ${isLight ? "border-amber-200 bg-amber-50 text-amber-900" : "border-amber-800/50 bg-amber-900/20 text-amber-200"}`}>
                      {resourceOptionsError}
                    </div>
                  )}
                  <select
                    value={dataModelId}
                    onChange={(e) => onResourcePick(e.target.value)}
                    disabled={resourceOptionsLoading || !!resourceOptionsError}
                    className={`w-full ${isLight ? 'bg-white border-neutral-300 text-neutral-900 shadow-sm' : 'bg-neutral-800 border-neutral-700 text-gray-200'} rounded px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-indigo-500 transition-all disabled:opacity-60`}
                  >
                    <option value="">
                      {resourceOptionsLoading
                        ? resCat === KNOWLEDGE_BASE_CATEGORY
                          ? "Loading knowledge bases…"
                          : "Loading data models…"
                        : resourceOptionsError
                          ? "Could not load options"
                          : "Select a resource…"}
                    </option>
                    {resCat === KNOWLEDGE_BASE_CATEGORY
                      ? knowledgeBases.map((kb) => (
                          <option key={kb.id} value={kb.id}>
                            {kb.title || kb.id}
                            {kb.description ? ` — ${kb.description}` : ""}
                          </option>
                        ))
                      : dataModels.map((dm) => (
                          <option key={dm.id} value={dm.id}>
                            {dm.modelName || dm.id}
                            {dm.description ? ` — ${dm.description}` : ""}
                          </option>
                        ))}
                  </select>
                  {!resourceOptionsLoading &&
                    !resourceOptionsError &&
                    resCat === KNOWLEDGE_BASE_CATEGORY &&
                    knowledgeBases.length === 0 && (
                      <p className={`mt-2 text-[11px] ${isLight ? "text-neutral-500" : "text-gray-500"}`}>
                        No knowledge bases returned. You cannot register until at least one exists.
                      </p>
                    )}
                  {!resourceOptionsLoading &&
                    !resourceOptionsError &&
                    resCat !== KNOWLEDGE_BASE_CATEGORY &&
                    dataModels.length === 0 && (
                      <p className={`mt-2 text-[11px] ${isLight ? "text-neutral-500" : "text-gray-500"}`}>
                        No data models returned. You cannot register until at least one model exists.
                      </p>
                    )}
                </div>

                <div>
                  <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'} font-medium mb-2`}>Resource details</p>
                  <div className="mb-3">
                    <label className={`block text-xs ${isLight ? 'text-neutral-500' : 'text-gray-500'} mb-1`}>Description</label>
                    <textarea
                      className={`w-full ${isLight ? 'bg-white border-neutral-300 text-neutral-900 shadow-sm' : 'bg-neutral-800 border-neutral-700 text-gray-200'} rounded px-3 py-1.5 text-xs placeholder:text-gray-400 outline-none focus:ring-1 focus:ring-indigo-500 resize-none transition-all`}
                      rows={3}
                      placeholder="What this resource exposes or how it should be used"
                      value={resDesc}
                      onChange={(e) => setResDesc(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className={`block text-xs ${isLight ? 'text-neutral-500' : 'text-gray-500'} mb-1`}>
                      Resource schema{" "}
                      <span className="font-normal text-gray-500">
                        (JSON, from selected resource — editable)
                      </span>
                    </label>
                    <textarea
                      spellCheck={false}
                      className={`w-full min-h-[220px] font-mono leading-relaxed ${isLight ? 'bg-neutral-50 border-neutral-300 text-neutral-900' : 'bg-neutral-950 border-neutral-700 text-gray-200'} rounded px-3 py-2 text-[11px] placeholder:text-gray-500 outline-none focus:ring-1 focus:ring-indigo-500 resize-y transition-all border shadow-inner`}
                      placeholder='Select a resource above to populate, or paste JSON…'
                      value={resourceSchemaText}
                      onChange={(e) => setResourceSchemaText(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className={`text-xs font-medium ${isLight ? 'text-neutral-600' : 'text-gray-400'}`}>Is Active</label>
                    <button
                      type="button"
                      onClick={() => setResIsActive((a) => !a)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isLight ? 'focus:ring-offset-2 focus:ring-offset-white' : 'focus:ring-offset-2 focus:ring-offset-neutral-900'} ${resIsActive ? "bg-green-600" : "bg-neutral-600"}`}
                      role="switch"
                      aria-checked={resIsActive}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${resIsActive ? "translate-x-5" : "translate-x-0.5"}`}
                        style={{ marginTop: 2 }}
                      />
                    </button>
                    <span className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-500'}`}>{resIsActive ? "Active" : "Inactive"}</span>
                  </div>
                </div>

                {/* actions */}
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={
                      (!editingResourceId && !dataModelId?.trim()) ||
                      !resName?.trim() ||
                      submitting
                    }
                    className={`flex items-center px-4 py-2 rounded text-sm font-medium shadow transition-colors ${flash ? "bg-green-600 text-white"
                      : ((!editingResourceId && !dataModelId?.trim()) || !resName?.trim() || submitting) ? "bg-indigo-900/40 text-indigo-500 cursor-not-allowed"
                        : "bg-indigo-600 hover:bg-indigo-700 text-white"
                      }`}
                  >
                    <PlusCircle className="w-4 h-4 mr-2" />
                    {flash
                      ? "✓ Saved!"
                      : submitting
                        ? editingResourceId
                          ? "Saving…"
                          : "Registering…"
                        : editingResourceId
                          ? "Save changes"
                          : "Register Resource"}
                  </button>
                  <button
                    type="button"
                    onClick={resetRegisterForm}
                    className={`px-4 py-2 rounded text-sm font-medium border ${isLight ? 'border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50' : 'border-neutral-700 bg-neutral-800 text-gray-400 hover:text-white hover:border-neutral-600'} transition-colors shadow-sm`}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={closeResourceForm}
                    className={`px-4 py-2 rounded text-sm font-medium border ${isLight ? 'border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50' : 'border-neutral-700 bg-neutral-800 text-gray-400 hover:text-white hover:border-neutral-600'} transition-colors shadow-sm`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── register tool popup ───────────────────────────────────── */}
      {showToolForm && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
          onClick={closeToolForm}
        >
          <div
            className={`${isLight ? "bg-white border-neutral-200" : "bg-neutral-900 border-neutral-800"} border rounded-lg shadow-xl w-full max-w-lg max-h-[95vh] overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`flex items-center justify-between px-4 py-3 ${isLight ? "bg-neutral-50 border-neutral-200" : "bg-neutral-900 border-neutral-800"} border-b flex-shrink-0`}
            >
              <div>
                <h3 className={`font-semibold ${isLight ? "text-neutral-900" : "text-white"} text-sm`}>
                  {editingToolId ? "Edit MCP Tool" : "Register MCP Tool"}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {editingToolId
                    ? "Change fields below and save your updates."
                    : "Add a tool developers can call from your MCP setup."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeToolForm}
                className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-neutral-800 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className={`block text-xs ${isLight ? "text-neutral-500" : "text-gray-500"} mb-1`}>Display name *</label>
                <input
                  className={`w-full ${isLight ? "bg-white border-neutral-300 text-neutral-900" : "bg-neutral-800 border-neutral-700 text-gray-200"} rounded px-3 py-1.5 text-xs placeholder:text-gray-400 outline-none focus:ring-1 focus:ring-violet-500 transition-all shadow-sm border`}
                  placeholder="e.g. Execution"
                  value={toolDisplayName}
                  onChange={(e) => setToolDisplayName(e.target.value)}
                />
              </div>
              <div>
                <label className={`block text-xs ${isLight ? "text-neutral-500" : "text-gray-500"} mb-1`}>Category</label>
                <select
                  value={toolCategory}
                  onChange={(e) => setToolCategory(e.target.value)}
                  className={`w-full ${isLight ? "bg-white border-neutral-300 text-neutral-900 shadow-sm" : "bg-neutral-800 border-neutral-700 text-gray-200"} rounded px-3 py-1.5 text-xs outline-none focus:ring-1 focus:ring-violet-500 transition-all`}
                >
                  {toolCategory && !(TOOL_CATEGORIES as readonly string[]).includes(toolCategory) ? (
                    <option value={toolCategory}>{toolCategory}</option>
                  ) : null}
                  {TOOL_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs ${isLight ? "text-neutral-500" : "text-gray-500"} mb-1`}>Endpoint *</label>
                <input
                  className={`w-full font-mono ${isLight ? "bg-white border-neutral-300 text-neutral-900" : "bg-neutral-800 border-neutral-700 text-gray-200"} rounded px-3 py-1.5 text-xs placeholder:text-gray-400 outline-none focus:ring-1 focus:ring-violet-500 transition-all shadow-sm`}
                  placeholder="e.g. Dynamic_Execution"
                  value={toolEndpoint}
                  onChange={(e) => setToolEndpoint(e.target.value)}
                />
              </div>
              <div>
                <label className={`block text-xs ${isLight ? "text-neutral-500" : "text-gray-500"} mb-1`}>Description</label>
                <textarea
                  className={`w-full ${isLight ? "bg-white border-neutral-300 text-neutral-900 shadow-sm" : "bg-neutral-800 border-neutral-700 text-gray-200"} rounded px-3 py-1.5 text-xs placeholder:text-gray-400 outline-none focus:ring-1 focus:ring-violet-500 resize-none transition-all`}
                  rows={3}
                  placeholder="Short description"
                  value={toolDescription}
                  onChange={(e) => setToolDescription(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-3">
                <label className={`text-xs font-medium ${isLight ? "text-neutral-600" : "text-gray-400"}`}>Is active</label>
                <button
                  type="button"
                  onClick={() => setToolIsActive((a) => !a)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 ${isLight ? "focus:ring-offset-2 focus:ring-offset-white" : "focus:ring-offset-2 focus:ring-offset-neutral-900"} ${toolIsActive ? "bg-green-600" : "bg-neutral-600"}`}
                  role="switch"
                  aria-checked={toolIsActive}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${toolIsActive ? "translate-x-5" : "translate-x-0.5"}`}
                    style={{ marginTop: 2 }}
                  />
                </button>
                <span className={`text-xs ${isLight ? "text-neutral-500" : "text-gray-500"}`}>{toolIsActive ? "Active" : "Inactive"}</span>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleToolSubmit}
                  disabled={!toolDisplayName.trim() || !toolEndpoint.trim() || submittingTool}
                  className={`flex items-center px-4 py-2 rounded text-sm font-medium shadow transition-colors ${toolFlash
                    ? "bg-green-600 text-white"
                    : !toolDisplayName.trim() || !toolEndpoint.trim() || submittingTool
                      ? "bg-violet-900/40 text-violet-500 cursor-not-allowed"
                      : "bg-violet-600 hover:bg-violet-700 text-white"
                    }`}
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  {toolFlash
                    ? "✓ Saved!"
                    : submittingTool
                      ? editingToolId
                        ? "Saving…"
                        : "Registering…"
                      : editingToolId
                        ? "Save changes"
                        : "Register tool"}
                </button>
                <button
                  type="button"
                  onClick={resetToolForm}
                  className={`px-4 py-2 rounded text-sm font-medium border ${isLight ? "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50" : "border-neutral-700 bg-neutral-800 text-gray-400 hover:text-white hover:border-neutral-600"} transition-colors shadow-sm`}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={closeToolForm}
                  className={`px-4 py-2 rounded text-sm font-medium border ${isLight ? "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50" : "border-neutral-700 bg-neutral-800 text-gray-400 hover:text-white hover:border-neutral-600"} transition-colors shadow-sm`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── sidebar + table (fills remaining viewport height) ───── */}
      <div className="flex min-h-0 flex-1 flex-col gap-5 sm:flex-row sm:items-stretch">
        <aside
          className={`flex shrink-0 flex-col overflow-hidden rounded-xl border shadow-sm sm:w-52 lg:w-56 sm:min-h-[calc(100vh-11rem)] ${isLight
            ? "border-neutral-200/90 bg-gradient-to-b from-neutral-50 via-white to-neutral-50/80"
            : "border-neutral-800 bg-gradient-to-b from-neutral-900 via-neutral-900/95 to-neutral-950"
            }`}
          aria-label="MCP list sections"
        >
          <div
            className={`border-b px-3 py-3 sm:py-3.5 ${isLight ? "border-neutral-200/80 bg-neutral-100/50" : "border-neutral-800/80 bg-neutral-950/40"
              }`}
          >
            <p
              className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${isLight ? "text-neutral-500" : "text-neutral-500"
                }`}
            >
              Catalog
            </p>
            <p className={`mt-0.5 text-xs ${isLight ? "text-neutral-600" : "text-neutral-400"}`}>MCP resources & tools</p>
          </div>
          <nav className="flex flex-1 flex-row gap-2 p-2 sm:flex-col sm:gap-1.5 sm:p-2.5">
            {(
              [
                {
                  id: "resources" as const,
                  label: "Resources",
                  hint: "API catalog",
                  count: resourceRows.length,
                  Icon: Layers,
                },
                {
                  id: "tools" as const,
                  label: "Tools",
                  hint: "Registrations",
                  count: toolRows.length,
                  Icon: Wrench,
                },
              ] as const
            ).map((item) => {
              const active = listSection === item.id;
              const Icon = item.Icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setListSection(item.id)}
                  className={`group flex w-full min-w-0 flex-1 items-center gap-3 rounded-lg border px-2.5 py-2.5 text-left transition-all sm:flex-none sm:py-3 ${active
                    ? isLight
                      ? "border-indigo-200/90 bg-indigo-50 shadow-md shadow-indigo-500/10 ring-1 ring-indigo-200/60"
                      : "border-indigo-500/35 bg-indigo-950/50 shadow-lg shadow-black/20 ring-1 ring-indigo-500/25"
                    : isLight
                      ? "border-transparent bg-white/0 hover:border-neutral-200 hover:bg-white"
                      : "border-transparent hover:border-neutral-700/80 hover:bg-neutral-800/40"
                    }`}
                  aria-current={active ? "page" : undefined}
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors ${active
                      ? isLight
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-indigo-600 text-white shadow-inner"
                      : isLight
                        ? "bg-neutral-200/80 text-neutral-600 group-hover:bg-neutral-200 group-hover:text-neutral-800"
                        : "bg-neutral-800 text-neutral-400 group-hover:bg-neutral-700 group-hover:text-neutral-200"
                      }`}
                  >
                    <Icon className="h-5 w-5" strokeWidth={active ? 2 : 1.75} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-sm font-semibold leading-tight ${active
                        ? isLight
                          ? "text-indigo-950"
                          : "text-indigo-100"
                        : isLight
                          ? "text-neutral-800"
                          : "text-neutral-200"
                        }`}
                    >
                      {item.label}
                    </span>
                    <span
                      className={`mt-0.5 block text-[10px] font-medium ${active
                        ? isLight
                          ? "text-indigo-600/80"
                          : "text-indigo-300/80"
                        : isLight
                          ? "text-neutral-500"
                          : "text-neutral-500"
                        }`}
                    >
                      {item.hint}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 tabular-nums rounded-md px-2 py-1 text-xs font-bold ${active
                      ? isLight
                        ? "bg-indigo-600 text-white"
                        : "bg-indigo-500 text-white"
                      : isLight
                        ? "bg-neutral-200/90 text-neutral-600"
                        : "bg-neutral-800 text-neutral-400"
                      }`}
                  >
                    {item.count}
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        <div
          className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border shadow-sm ${isLight ? "border-neutral-200 bg-white" : "border-neutral-800 bg-neutral-900/30"
            }`}
        >
          <div
            className={`shrink-0 border-b px-4 py-3 flex flex-wrap items-center gap-3 ${isLight ? "border-neutral-200 bg-neutral-50/80" : "border-neutral-800 bg-neutral-950/40"
              }`}
          >
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none ${isLight ? "text-neutral-400" : "text-gray-500"}`}
                aria-hidden
              />
              <input
                type="search"
                value={activeSearch}
                onChange={(e) => setActiveSearch(e.target.value)}
                placeholder={listSection === "resources" ? "Search resources by name, ID, category…" : "Search tools by name, ID, endpoint…"}
                className={searchInputClass}
                aria-label={listSection === "resources" ? "Search resources" : "Search tools"}
              />
            </div>
            {activeSearch.trim() && (
              <span className={`text-xs tabular-nums ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
                {activeFilteredCount} of {activeTotalCount}
              </span>
            )}
            {activeSearch.trim() && (
              <button
                type="button"
                onClick={() => setActiveSearch("")}
                className={`text-xs font-medium transition-colors ${isLight ? "text-indigo-600 hover:text-indigo-800" : "text-indigo-400 hover:text-indigo-300"}`}
              >
                Clear
              </button>
            )}
            <label className={`flex items-center gap-2 text-xs shrink-0 ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
              <span className="whitespace-nowrap">Rows</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className={pageSizeSelectClass}
                aria-label="Rows per page"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto">
            {loading ? (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">Loading…</div>
          ) : listSection === "resources" ? (
            resourceRows.length === 0 ? (
              <div className={`px-4 py-10 text-center text-sm ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
                No MCP resources registered yet.
              </div>
            ) : filteredResourceRows.length === 0 ? (
              <div className={`px-4 py-10 text-center text-sm ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
                No resources match &quot;{resourceSearch.trim()}&quot;.
              </div>
            ) : (
              <table className={`min-w-full text-sm text-left ${isLight ? "text-neutral-800" : "text-gray-300"}`}>
                <thead className={isLight ? "bg-neutral-100 text-neutral-600" : "bg-neutral-900 text-gray-400"}>
                  <tr>
                    {["Resource ID", "Name", "Category", "Type", "Description", "Created", "Updated", "Is Active", "Actions"].map((h) => (
                      <th key={h} className="px-4 py-2 text-xs font-medium">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedResourceRows.map((r) => (
                    <tr
                      key={r.id}
                      className={`border-t ${isLight ? "border-neutral-200 hover:bg-neutral-50" : "border-neutral-800 hover:bg-neutral-800/30"}`}
                    >
                      <td className={`px-4 py-3 font-mono text-xs ${isLight ? "text-indigo-600" : "text-indigo-400"}`}>{r.resourceId}</td>
                      <td className={`px-4 py-3 ${isLight ? "text-neutral-900" : "text-gray-200"}`}>{r.name}</td>
                      <td className={`px-4 py-3 capitalize ${isLight ? "text-neutral-600" : "text-gray-400"}`}>{r.cat}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${isLight ? "bg-neutral-200 text-neutral-700" : "bg-neutral-700 text-gray-300"
                            }`}
                        >
                          {r.type}
                        </span>
                      </td>
                      <td className={`px-4 py-3 max-w-[220px] ${isLight ? "text-neutral-600" : "text-gray-400"}`}>
                        <span className="block truncate text-xs" title={r.description ?? undefined}>
                          {r.description ?? "—"}
                        </span>
                      </td>
                      <td className={`px-4 py-3 font-mono text-xs ${isLight ? "text-neutral-500" : "text-gray-500"}`}>
                        {r.createdAt && !Number.isNaN(Date.parse(r.createdAt))
                          ? new Date(r.createdAt).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className={`px-4 py-3 font-mono text-xs ${isLight ? "text-neutral-500" : "text-gray-500"}`}>
                        {r.updatedAt && !Number.isNaN(Date.parse(r.updatedAt))
                          ? new Date(r.updatedAt).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setConfirmToggle({ id: r.id, newValue: !r.isActive })}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isLight ? "focus:ring-offset-2 focus:ring-offset-white" : "focus:ring-offset-2 focus:ring-offset-neutral-900"
                            } ${r.isActive ? "bg-green-600" : "bg-neutral-600"}`}
                          role="switch"
                          aria-checked={r.isActive}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${r.isActive ? "translate-x-5" : "translate-x-0.5"}`}
                            style={{ marginTop: 2 }}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="text-yellow-400 hover:text-white transition-colors"
                            title="Edit"
                            onClick={() => openEditResource(r)}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            className="text-red-400 hover:text-red-200 transition-colors"
                            title="Delete"
                            onClick={async () => {
                              try {
                                const { assignedTokenCount } = await mcpResourcesService.getAssignedTokenCount(r.id);
                                setDeleteConfirm({ id: r.id, name: r.name, assignedTokenCount });
                              } catch {
                                setDeleteConfirm({ id: r.id, name: r.name, assignedTokenCount: 0 });
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : toolRows.length === 0 ? (
            <div className={`px-4 py-10 text-center text-sm ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
              No MCP tools registered yet.
            </div>
          ) : filteredToolRows.length === 0 ? (
            <div className={`px-4 py-10 text-center text-sm ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
              No tools match &quot;{toolSearch.trim()}&quot;.
            </div>
          ) : (
            <table className={`min-w-full text-sm text-left ${isLight ? "text-neutral-800" : "text-gray-300"}`}>
              <thead className={isLight ? "bg-neutral-100 text-neutral-600" : "bg-neutral-900 text-gray-400"}>
                <tr>
                  {["Tool ID", "Display Name", "Category", "Endpoint", "Description", "Created", "Updated", "Active", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-2 text-xs font-medium whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedToolRows.map((t) => (
                  <tr
                    key={t.id}
                    className={`border-t ${isLight ? "border-neutral-200 hover:bg-neutral-50" : "border-neutral-800 hover:bg-neutral-800/30"}`}
                  >
                    <td className={`px-4 py-3 font-mono text-xs ${isLight ? "text-indigo-600" : "text-indigo-400"}`}>{t.toolId}</td>
                    <td className={`px-4 py-3 ${isLight ? "text-neutral-900" : "text-gray-200"}`}>{t.displayName}</td>
                    <td className={`px-4 py-3 ${isLight ? "text-neutral-600" : "text-gray-400"}`}>{t.category}</td>
                    <td className={`px-4 py-3 font-mono text-xs ${isLight ? "text-neutral-600" : "text-gray-400"}`}>{t.endpoint}</td>
                    <td className={`px-4 py-3 max-w-[200px] ${isLight ? "text-neutral-600" : "text-gray-400"}`}>
                      <span className="block truncate text-xs" title={t.description ?? undefined}>
                        {t.description ?? "—"}
                      </span>
                    </td>
                    <td className={`px-4 py-3 font-mono text-xs whitespace-nowrap ${isLight ? "text-neutral-500" : "text-gray-500"}`}>
                      {t.createdDate ? new Date(t.createdDate).toLocaleString() : "—"}
                    </td>
                    <td className={`px-4 py-3 font-mono text-xs whitespace-nowrap ${isLight ? "text-neutral-500" : "text-gray-500"}`}>
                      {t.updatedDate ? new Date(t.updatedDate).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${t.isActive
                          ? isLight
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-emerald-900/40 text-emerald-300"
                          : isLight
                            ? "bg-neutral-200 text-neutral-600"
                            : "bg-neutral-700 text-gray-400"
                          }`}
                      >
                        {t.isActive ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-yellow-400 hover:text-white transition-colors"
                          title="Edit"
                          onClick={() => openEditTool(t)}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="text-red-400 hover:text-red-200 transition-colors"
                          title="Delete"
                          onClick={() =>
                            setDeleteToolConfirm({ id: t.id, name: t.displayName || t.toolId })
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          </div>
          {!loading && activeFilteredCount > 0 && (
            <div
              className={`shrink-0 border-t px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${isLight ? "border-neutral-200 bg-neutral-50/80" : "border-neutral-800 bg-neutral-950/40"
                }`}
            >
              <p className={`text-xs tabular-nums ${isLight ? "text-neutral-500" : "text-gray-400"}`}>
                Showing {activeRangeStart}–{activeRangeEnd} of {activeFilteredCount}{" "}
                {listSection === "resources" ? "resources" : "tools"}
                {activeTotalCount !== activeFilteredCount ? ` (${activeTotalCount} total)` : ""}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActivePage((p) => Math.max(1, p - 1))}
                  disabled={activePage <= 1}
                  className={paginationBtnClass}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className={`min-w-[88px] text-center text-xs tabular-nums ${isLight ? "text-neutral-600" : "text-gray-300"}`}>
                  Page {activePage} of {activeTotalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setActivePage((p) => Math.min(activeTotalPages, p + 1))}
                  disabled={activePage >= activeTotalPages}
                  className={paginationBtnClass}
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirm Is Active toggle */}
      {confirmToggle !== null && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-700'} border rounded-lg shadow-xl max-w-sm w-full p-5`}>
            <h4 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'} mb-1`}>
              {confirmToggle.newValue ? "Activate resource" : "Deactivate resource"}
            </h4>
            <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'} mb-4`}>
              {confirmToggle.newValue
                ? "This resource will be active and available to developers."
                : "This resource will be inactive and unavailable until activated again."}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirmToggle(null)}
                className={`px-3 py-1.5 rounded text-xs font-medium border ${isLight ? 'border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50' : 'border-neutral-600 bg-neutral-800 text-gray-300 hover:bg-neutral-700'} transition-colors`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyToggleResourceActive}
                className={`px-3 py-1.5 rounded text-xs font-medium text-white transition-colors ${confirmToggle.newValue ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
              >
                {confirmToggle.newValue ? "Activate" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirm Delete */}
      {deleteConfirm !== null && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-700'} border rounded-lg shadow-xl max-w-sm w-full p-5`}>
            <h4 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'} mb-1`}>Delete resource</h4>
            <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'} mb-2`}>
              Are you sure you want to delete <span className={`${isLight ? 'text-indigo-600' : 'text-indigo-400'} font-semibold`}>&quot;{deleteConfirm.name}&quot;</span>? This action cannot be undone.
            </p>
            {deleteConfirm.assignedTokenCount > 0 && (
              <p className={`text-xs rounded-lg px-3 py-2.5 mb-4 border leading-relaxed ${isLight
                  ? 'bg-amber-50 text-amber-800 border-amber-200 shadow-sm'
                  : 'bg-amber-900/20 text-amber-300 border-amber-700/30'
                }`}>
                <span className="font-bold block mb-0.5">Warning</span>
                This resource is assigned to <strong>{deleteConfirm.assignedTokenCount}</strong> developer token(s). Deleting will not revoke those tokens; they will reference a removed resource.
              </p>
            )}
            {deleteConfirm.assignedTokenCount === 0 && <div className="mb-4" />}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className={`px-3 py-1.5 rounded text-xs font-medium border ${isLight ? 'border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50' : 'border-neutral-600 bg-neutral-800 text-gray-300 hover:bg-neutral-700'} transition-colors`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!deleteConfirm) return;
                  await handleDelete(deleteConfirm.id);
                  setDeleteConfirm(null);
                }}
                className="px-3 py-1.5 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {deleteToolConfirm !== null && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`${isLight ? "bg-white border-neutral-200" : "bg-neutral-900 border-neutral-700"} border rounded-lg shadow-xl max-w-sm w-full p-5`}>
            <h4 className={`text-sm font-semibold ${isLight ? "text-neutral-900" : "text-white"} mb-1`}>Delete tool</h4>
            <p className={`text-xs ${isLight ? "text-neutral-500" : "text-gray-400"} mb-4`}>
              Are you sure you want to delete{" "}
              <span className={`${isLight ? "text-violet-700" : "text-violet-300"} font-semibold`}>
                &quot;{deleteToolConfirm.name}&quot;
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setDeleteToolConfirm(null)}
                className={`px-3 py-1.5 rounded text-xs font-medium border ${isLight ? "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50" : "border-neutral-600 bg-neutral-800 text-gray-300 hover:bg-neutral-700"} transition-colors`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!deleteToolConfirm) return;
                  await handleDeleteTool(deleteToolConfirm.id);
                  setDeleteToolConfirm(null);
                }}
                className="px-3 py-1.5 rounded text-xs font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}